import type { SubQuestion } from '../../../types/helios/Question';
import { isEditableOptionType } from './subQuestionResponseType';
import { countNonEmptyOptions, MIN_OPTIONS } from './optionsValidation';
import { isHtmlContentEmpty } from 'src/validations/translationFormSchema';

/**
 * Pre-publish validation for a question's sub-questions on the AuditWhiz
 * Review flow. The review flow edits live (no per-sub Save button), so these
 * rules can't be enforced at a commit moment the way the manual (v1) flow did
 * at its side-panel Save — instead the publish action gates on them, the row
 * headers flag violating subs, and this module is the single source of truth
 * both read from.
 *
 * House style mirrors {@link auditClosureValidation}: a pure function
 * returning an ARRAY of typed violation records, plus a per-row predicate.
 * Pure — no react, no side effects. Extensible: add a new `kind` + rule as
 * more blockers appear (empty title, etc.).
 */

/** Discriminated reasons a sub-question isn't ready to publish. Each is a
 *  faithful port of a V1 (manual flow) `SubQuestionSidePanel.isFormValid`
 *  rule — see tasks/audit-whiz/validations.md. */
export const SubQuestionViolationKind = {
  /** The sub-question title is blank (whitespace-only counts as blank). */
  EMPTY_TITLE: 'EMPTY_TITLE',
  /** The sub-question instruction is blank. */
  EMPTY_INSTRUCTION: 'EMPTY_INSTRUCTION',
  /** A SINGLE_SELECT / MULTI_SELECT sub has fewer than {@link MIN_OPTIONS}
   *  non-empty answer options. */
  MIN_OPTIONS: 'MIN_OPTIONS',
  /** The sub has an attached task whose required fields aren't complete:
   *  a blank task name or description, or a non-positive duration. Only
   *  applies when a task is present (task itself is optional). */
  TASK_INCOMPLETE: 'TASK_INCOMPLETE',
} as const;

export type SubQuestionViolationKind =
  (typeof SubQuestionViolationKind)[keyof typeof SubQuestionViolationKind];

/** Which required field of an attached task is missing. The banner names only
 *  the fields actually blank (V1 reports task errors per field), so a partial
 *  fill shows "add a task duration", not all three. */
export const TaskField = {
  NAME: 'NAME',
  DESCRIPTION: 'DESCRIPTION',
  DURATION: 'DURATION',
} as const;

export type TaskField = (typeof TaskField)[keyof typeof TaskField];

/** One violation on one sub-question. `index` is 1-based to match the row
 *  header's visible "N." numbering (array position, not `orderIndex`). */
export interface SubQuestionViolation {
  readonly index: number;
  readonly title: string;
  readonly kind: SubQuestionViolationKind;
  /** Only set for {@link SubQuestionViolationKind.TASK_INCOMPLETE}: the task
   *  fields that are blank, in display order (name, description, duration).
   *  Non-empty whenever present. */
  readonly missingTaskFields?: readonly TaskField[];
}

/** The required task fields that are blank, in display order — empty when the
 *  task is complete or absent. Mirrors V1's `hasTask` gate: a task is OPTIONAL,
 *  but once attached its name + description + positive duration are required.
 *  Returning the LIST (not a bool) lets the banner name only what's missing and
 *  feeds the per-field inline errors. */
function missingTaskFields(sub: SubQuestion): readonly TaskField[] {
  const task = sub.taskDetails;
  if (!task) return [];
  const missing: TaskField[] = [];
  if (task.taskName.trim().length === 0) missing.push(TaskField.NAME);
  // taskDescription is a rich-text (HTML) field, so a cleared editor stores
  // "<p></p>". Reuse the canonical V1 emptiness check — which uses
  // allowImageOnly here because the manual flow lets auditors submit a
  // description that is ONLY a pasted screenshot (see taskDraftFormSchema).
  if (isHtmlContentEmpty(task.taskDescription, { allowImageOnly: true })) {
    missing.push(TaskField.DESCRIPTION);
  }
  if ((task.taskDurationDays ?? 0) <= 0) missing.push(TaskField.DURATION);
  return missing;
}

/** The extra, kind-specific fields a rule contributes to its violation record
 *  (beyond `index`/`title`/`kind`). Empty for rules that need no detail. */
type ViolationDetail = Pick<SubQuestionViolation, 'missingTaskFields'>;

/** The ordered rule set applied to each sub-question. Each entry pairs its
 *  `kind` with a `detect` that returns the violation DETAIL when the rule is
 *  broken, or `null` when it passes. Declared once so the per-sub loop stays
 *  flat and new rules are added in one place. */
const SUB_QUESTION_RULES: readonly {
  readonly kind: SubQuestionViolationKind;
  readonly detect: (sub: SubQuestion) => ViolationDetail | null;
}[] = [
  {
    kind: SubQuestionViolationKind.EMPTY_TITLE,
    detect: (sub) => (sub.title.trim().length === 0 ? {} : null),
  },
  {
    // The instruction is a rich-text (HTML) field, so a visually-cleared
    // editor still stores markup like "<p></p>". Reuse the canonical V1
    // emptiness check (strict — NO allowImageOnly, matching the manual flow:
    // an instruction must have text, an image-only instruction is not valid).
    kind: SubQuestionViolationKind.EMPTY_INSTRUCTION,
    detect: (sub) => (isHtmlContentEmpty(sub.instruction) ? {} : null),
  },
  {
    kind: SubQuestionViolationKind.MIN_OPTIONS,
    detect: (sub) =>
      isEditableOptionType(sub.responseType) &&
      countNonEmptyOptions(sub.options ?? []) < MIN_OPTIONS
        ? {}
        : null,
  },
  {
    // Carries WHICH task fields are blank so the banner names only those.
    kind: SubQuestionViolationKind.TASK_INCOMPLETE,
    detect: (sub) => {
      const missing = missingTaskFields(sub);
      return missing.length > 0 ? { missingTaskFields: missing } : null;
    },
  },
];

/**
 * Returns every publish-blocking violation across a question's sub-questions,
 * in row order (a single sub can contribute MULTIPLE violations — e.g. an
 * empty title AND too few options). Empty array === ready to publish.
 *
 * Rules (faithful ports of V1 `SubQuestionSidePanel.isFormValid`, see
 * tasks/audit-whiz/validations.md): title required, instruction required
 * (rich-text — checked via the canonical isHtmlContentEmpty, strict/no
 * image-only), editable select types (SINGLE_SELECT / MULTI_SELECT, NOT
 * YES_NO) need at
 * least {@link MIN_OPTIONS} non-empty options, and an attached task must have
 * name + description + positive duration. Reuses {@link countNonEmptyOptions}
 * so the gate, the inline editor hint, and the row flag all measure
 * identically.
 */
export function validateSubQuestions(
  subs: readonly SubQuestion[],
): SubQuestionViolation[] {
  const violations: SubQuestionViolation[] = [];
  subs.forEach((sub, i) => {
    for (const rule of SUB_QUESTION_RULES) {
      const detail = rule.detect(sub);
      if (detail !== null) {
        violations.push({
          index: i + 1,
          title: sub.title,
          kind: rule.kind,
          ...detail,
        });
      }
    }
  });
  return violations;
}

/**
 * Whether the sub-question at 1-based `index` has any violation — the row
 * header's "incomplete" indicator reads this. Kept as its own predicate
 * (rather than the row re-deriving) so the row and the gate agree.
 */
export function subQuestionHasViolation(
  violations: readonly SubQuestionViolation[],
  index: number,
): boolean {
  return violations.some((v) => v.index === index);
}

/** Per-field inline-error flags for one sub-question's editor fields. Each is
 *  true when that specific field is blocking publish, so the field can show the
 *  native red error state. `options` isn't here — the options editor derives
 *  its own hint from the identical MIN_OPTIONS measure in
 *  {@link SubQuestionDataCollectionFields}. */
export interface SubQuestionFieldErrors {
  readonly title: boolean;
  readonly instruction: boolean;
  readonly taskName: boolean;
  readonly taskDescription: boolean;
  readonly taskDuration: boolean;
}

/**
 * The per-field error flags for the sub-question at 1-based `index`, derived
 * from the SAME {@link validateSubQuestions} output the gate and banner read —
 * so an inline field error, the row badge, and the banner can never disagree.
 * Everything is false when the sub has no violations (nothing is flagged until
 * it actually blocks publish), and each flag clears the instant its field is
 * filled because the whole result is recomputed live.
 */
export function subQuestionFieldErrors(
  violations: readonly SubQuestionViolation[],
  index: number,
): SubQuestionFieldErrors {
  const forSub = violations.filter((v) => v.index === index);
  const hasKind = (kind: SubQuestionViolationKind): boolean =>
    forSub.some((v) => v.kind === kind);
  const missingTaskField = (field: TaskField): boolean =>
    forSub.some((v) => v.missingTaskFields?.includes(field) ?? false);
  return {
    title: hasKind(SubQuestionViolationKind.EMPTY_TITLE),
    instruction: hasKind(SubQuestionViolationKind.EMPTY_INSTRUCTION),
    taskName: missingTaskField(TaskField.NAME),
    taskDescription: missingTaskField(TaskField.DESCRIPTION),
    taskDuration: missingTaskField(TaskField.DURATION),
  };
}
